import React from "react";

interface MarkdownContentProps {
  content: string;
}

function renderInline(text: string): React.ReactNode[] {
  // Processa links [texto](url) e negrito **texto** de forma segura
  const parts: React.ReactNode[] = [];
  const regex = /(\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]+)\))|(\*\*([^*]+)\*\*)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    if (match[1]) {
      // Link seguro
      const linkText = match[2];
      const linkUrl = match[3];
      const isExternal = linkUrl.startsWith("http");
      parts.push(
        <a
          key={match.index}
          href={linkUrl}
          {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        >
          {linkText}
        </a>
      );
    } else if (match[4]) {
      // Negrito
      const boldText = match[5];
      parts.push(<strong key={match.index}>{boldText}</strong>);
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

export function MarkdownContent({ content }: MarkdownContentProps) {
  if (!content) return null;

  // Divide por blocos separados por linha dupla
  const blocks = content.trim().split(/\n\s*\n/);

  return (
    <div className="case-markdown">
      {blocks.map((block, index) => {
        const trimmed = block.trim();
        if (!trimmed) return null;

        if (trimmed.startsWith("### ")) {
          return <h3 key={index}>{renderInline(trimmed.slice(4))}</h3>;
        }
        if (trimmed.startsWith("## ")) {
          return <h2 key={index}>{renderInline(trimmed.slice(3))}</h2>;
        }
        if (trimmed.startsWith("# ")) {
          return <h1 key={index}>{renderInline(trimmed.slice(2))}</h1>;
        }

        // Listas não ordenadas
        const lines = trimmed.split("\n");
        if (lines.length > 0 && lines.every((line) => line.trim().startsWith("- ") || line.trim().startsWith("* "))) {
          const items = lines.map((line) => line.trim().replace(/^[-*]\s+/, ""));
          return (
            <ul key={index}>
              {items.map((item, itemIdx) => (
                <li key={itemIdx}>{renderInline(item)}</li>
              ))}
            </ul>
          );
        }

        return <p key={index}>{renderInline(trimmed)}</p>;
      })}
    </div>
  );
}
